import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../contexts/MessageContext';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

const ChatWindowContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  background-color: white;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primaryLight};
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
`;

const UserInfo = styled.div`
  flex-grow: 1;
`;

const UserName = styled.div`
  font-weight: bold;
  margin-bottom: 0.25rem;
`;

const UserStatus = styled.div`
  font-size: 0.8rem;
  opacity: 0.8;
`;

const MessagesContainer = styled.div`
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ChatWindow = ({ partnerId, clientId, isPartnerView = false }) => {
  const { currentUser } = useAuth();
  const { markAsRead } = useMessages();
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  // Determine the correct user ID to fetch based on the view
  const userId = isPartnerView ? clientId : partnerId;
  
  // Fetch user data (either partner or client depending on view)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Determine which collection to fetch from based on the view
        const collectionName = isPartnerView ? 'users' : 'partners';
        const userDoc = await getDoc(doc(db, collectionName, userId));
        
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } catch (error) {
        console.error(`Error fetching ${isPartnerView ? 'client' : 'partner'} data:`, error);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId, isPartnerView]);

  // Find or create conversation
  useEffect(() => {
    if (!currentUser || !userId) return;

    const findConversation = async () => {
      setLoading(true);
      try {
        const conversationsRef = collection(db, 'conversations');
        
        // Query structure depends on whether we're in partner view or customer view
        const q = isPartnerView
          ? query(
              conversationsRef,
              where('userId', '==', clientId),
              where('partnerId', '==', currentUser.uid)
            )
          : query(
              conversationsRef,
              where('userId', '==', currentUser.uid),
              where('partnerId', '==', partnerId)
            );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
          if (snapshot.empty) {
            // Create a new conversation
            const newConversationData = {
              userId: isPartnerView ? clientId : currentUser.uid,
              partnerId: isPartnerView ? currentUser.uid : partnerId,
              createdAt: serverTimestamp(),
              lastMessageTimestamp: serverTimestamp(),
              lastMessage: 'Start your conversation',
              isUserTyping: false,
              isPartnerTyping: false
            };
            
            const newConversationRef = await addDoc(conversationsRef, newConversationData);
            setConversationId(newConversationRef.id);
          } else {
            setConversationId(snapshot.docs[0].id);
          }
          setLoading(false);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error finding conversation:', error);
        setLoading(false);
      }
    };

    findConversation();
  }, [currentUser, userId, partnerId, clientId, isPartnerView]);

  // Load messages
  useEffect(() => {
    if (!conversationId || !currentUser) return;

    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('conversationId', '==', conversationId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
      
      // Mark conversation as read when messages are loaded
      if (messagesData.length > 0) {
        // Only mark as read if we're in partner view and there are unread messages
        const hasUnreadMessages = messagesData.some(
          msg => !msg.isRead && msg.receiverId === currentUser.uid
        );
        
        if (hasUnreadMessages) {
          console.log('Marking conversation as read due to viewing');
          markAsRead(conversationId);
        }
      }
    });

    return unsubscribe;
  }, [conversationId, currentUser, markAsRead]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (content) => {
    if (!content.trim() || !conversationId) return;

    try {
      // Determine sender and receiver IDs based on the view
      const senderId = currentUser.uid;
      const receiverId = isPartnerView ? clientId : partnerId;
      
      // Add message to the messages collection
      await addDoc(collection(db, 'messages'), {
        conversationId,
        senderId,
        receiverId,
        content,
        timestamp: serverTimestamp(),
        isRead: false,
        attachments: []
      });

      // Update conversation with last message
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: content,
        lastMessageTimestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  if (loading) {
    return <div>Loading chat...</div>;
  }

  return (
    <ChatWindowContainer>
      <ChatHeader>
        <Avatar>
          {userData?.displayName?.charAt(0).toUpperCase() || (isPartnerView ? 'C' : 'P')}
        </Avatar>
        <UserInfo>
          <UserName>{userData?.displayName || (isPartnerView ? 'Client' : 'Partner')}</UserName>
          <UserStatus>
            {userData?.online ? 'Online' : 'Offline'}
          </UserStatus>
        </UserInfo>
      </ChatHeader>
      
      <MessagesContainer>
        {messages.map(message => (
          <ChatMessage 
            key={message.id}
            message={message}
            isOwn={message.senderId === currentUser.uid}
          />
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      
      <ChatInput onSendMessage={handleSendMessage} />
    </ChatWindowContainer>
  );
};

export default ChatWindow; 